import React, { useEffect } from "react";
import { Alert, Box, Button, Card, CardContent, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getContentLanguage } from "../../services/LangPrefs";
import { trackEvent } from "../../services/GoogleAnalytics";

export default function QuickStartPage() {
    const navigate = useNavigate();
    const lang = getContentLanguage() || "english";

    useEffect(() => {
        trackEvent("entered", { name: "QuickStartPage" });
    }, []);

    const handleNewPuzzle = (type) => {
        navigate(`/creator/grid/edit?language=${lang}&type=${type}`);
    };

    return (
        <Box sx={{ maxWidth: 720, mx: "auto", p: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "primary.main", mb: 2 }}>
                Quick Start: Creator Guide
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                This guide is for Creators. It shows how to create a puzzle by filling all empty cells in a grid (Word Search or Hidden Message).
            </Typography>

            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <Box component="span" sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "primary.main", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.9rem" }}>1</Box>
                        Select a puzzle type
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 5, mb: 1 }}>
                        Click <strong>&quot;New Hidden Message&quot;</strong> or <strong>&quot;New Word Search&quot;</strong>. A puzzle with empty cells will appear. This guide shows how to fill all of them.
                    </Typography>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <Box component="span" sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "primary.main", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.9rem" }}>2</Box>
                        Click the Fill button
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 5 }}>
                        Click the <strong>Fill</strong> button on the edit page. It opens a dialog where you can find and place words.
                    </Typography>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <Box component="span" sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "primary.main", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.9rem" }}>3</Box>
                        Find words and place them(Tabs 1, 2, 3)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 5, mb: 1 }}>
                        The <AutoAwesomeIcon sx={{ fontSize: 16, verticalAlign: "middle" }} /> icon means AI. Use:
                    </Typography>
                    <Box component="ul" sx={{ pl: 6, mb: 1, "& li": { mb: 0.5 } }}>
                        <li><strong>Tab 1</strong> — AI finds words more associated with words already in the puzzle</li>
                        <li><strong>Tab 2</strong> — AI finds less-associated words</li>
                        <li><strong>Tab 3</strong> — Find words in the dictionary; they may not be related. Choose carefully.</li>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 5, mb: 1 }}>
                        For <strong>Hidden Message</strong> puzzles, use the down arrow <ExpandMoreIcon sx={{ fontSize: 18, verticalAlign: "middle" }} /> button to see a list of words in the <strong>Words not in use</strong> section. One can be the hidden message—it will be scattered in the puzzle.
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        <strong>Important for Hidden Message:</strong> The puzzle must have the message. Add the word from the &quot;Words not in use&quot; section. The empty cell count and the character count of the word must be the same. Try to put the long word for the hidden message.
                    </Alert>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 5, mb: 1, mt: 1 }}>
                        After finding a word, click <strong>Place</strong> to put it into the grid.
                    </Typography>
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                        <strong>Tip:</strong> Try Tab 1 → Tab 2 → Tab 3. After placing a word, click <strong>Find</strong> again. If there are no candidates, move to the next tab.
                    </Alert>
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                        <strong>Important:</strong> Words you select should be related to the puzzle&apos;s title or tags. This helps create a coherent, themed puzzle that fits the topic. Avoid proper nouns (e.g. &quot;RCAF&quot;, &quot;RCIA&quot;).
                    </Alert>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <Box component="span" sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "primary.main", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.9rem" }}>4</Box>
                        Verify and submit
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 5 }}>
                        After filling all empty cells, you&apos;ll see the <strong>Full Filled</strong> box and the <strong>Ready to Review</strong> button. Click it to open the Verify Puzzle dialog. Check that the puzzle is valid, then click <strong>Confirm &amp; Proceed</strong>. That&apos;s it!
                    </Typography>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 3, borderColor: "primary.main", borderWidth: 2 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: "primary.main" }}>
                        Puzzle workflow: 4 steps
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>DRAFT</strong> → <strong>READY TO REVIEW</strong> → <strong>REVIEWED</strong> → <strong>PUBLISHED</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            After filling all empty cells in <strong>DRAFT</strong>, you (as Creator) can change the state to <strong>Ready to Review</strong>. A Manager will then change it to <strong>Reviewed</strong>, and the system publishes the puzzle on schedule.
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleNewPuzzle("HIDDEN_MESSAGE")}
                    startIcon={<CheckCircleIcon />}
                    sx={{ whiteSpace: "nowrap" }}
                >
                    New Hidden Message
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleNewPuzzle("WORD_SEARCH")}
                    startIcon={<CheckCircleIcon />}
                    sx={{ whiteSpace: "nowrap" }}
                >
                    New Word Search
                </Button>
            </Box>
        </Box>
    );
}
